/**
 * How words handed back to the agent composer join, and leave, the text already there. Words come
 * back when a send is refused or abandoned and when Stop returns queued guidance; they always land
 * after what the user has typed since, separated by a blank line, and Retry takes them out again.
 */

/** The composer text with `added` appended after whatever is already typed. */
export function appendToComposerText(existing: string, added: string): string {

  const current = existing.trim();
  return current ? `${current}\n\n${added}` : added;
}

/**
 * The composer text with a handed-back request taken out again, for Retry, which resends that
 * request itself. Only an intact copy goes: the words standing as their own entry, with nothing but
 * a blank line or the edge of the text on either side, which is how a hand-back lands. Text typed
 * around them stays, and a request the user has since edited no longer matches and is left alone
 * rather than cut down to whatever follows the original words.
 */
export function removeFromComposerText(existing: string, removed: string): string {

  const words = removed.trim();
  if (!words) return existing;
  // Latest copy first: a hand-back lands after what is already typed.
  let index = existing.lastIndexOf(words);
  while (index !== -1) {
    const before = existing.slice(0, index);
    const after = existing.slice(index + words.length);
    if (isEntryEdge(before, 'before') && isEntryEdge(after, 'after')) {
      return [before.trimEnd(), after.trimStart()].filter(Boolean).join('\n\n');
    }
    index = index === 0 ? -1 : existing.lastIndexOf(words, index - 1);
  }
  return existing;
}

/** Whether the text beside a match ends (or starts) an entry: empty, or a blank line away. */
function isEntryEdge(text: string, side: 'before' | 'after'): boolean {

  if (!text.trim()) return true;
  return side === 'before' ? /\n[ \t]*\n\s*$/.test(text) : /^\s*\n[ \t]*\n/.test(text);
}
